import { action, makeObservable, observable, runInAction } from 'mobx'
import { Cubit } from '../core'

export type DetailedPermissionState =
  | 'initializing'
  | 'waiting'
  | 'denied'
  | 'granted'
  | 'NotAllowed'
  | 'NotReadable'
  | 'Overconstrained'
  | 'Security'

/**
 * Manages browser media permissions and local audio/video tracks.
 *
 * Owns the getUserMedia lifecycle so that tracks survive React component
 * unmounts (e.g. AvCheck → MeetingPage transition in Firefox).
 */
export class AvMediaManagementCubit extends Cubit {
  @observable localAudioTrack: MediaStreamTrack | null = null
  @observable localVideoTrack: MediaStreamTrack | null = null
  @observable localAudioDeviceId: string = ''
  @observable localVideoDeviceId: string = ''
  @observable mediaPermissionState: DetailedPermissionState = 'initializing'
  @observable audioPermissionState: DetailedPermissionState = 'initializing'
  @observable videoPermissionState: DetailedPermissionState = 'initializing'

  constructor(
    private logEvent: (name: string, params?: Record<string, unknown>) => void
  ) {
    super()
    makeObservable(this)
  }

  dispose() {
    this.stopLocalTracks()
    return super.dispose()
  }

  // ---------------------------------------------------------------------------
  // Permission requests
  // ---------------------------------------------------------------------------

  async requestMediaPermissions(preferredDevices?: {
    audioDeviceId?: string
    videoDeviceId?: string
  }) {
    if (this.isDisposed) return

    // Stop any previously-captured tracks to avoid leaking active captures
    this.stopLocalTracks()

    const timeout = setTimeout(() => {
      if (!this.isDisposed) {
        runInAction(() => {
          this.mediaPermissionState = 'waiting'
        })
      }
    }, 1000)

    const audioConstraint: MediaTrackConstraints | boolean =
      preferredDevices?.audioDeviceId
        ? { deviceId: preferredDevices.audioDeviceId }
        : true
    const videoConstraint: MediaTrackConstraints | boolean =
      preferredDevices?.videoDeviceId
        ? { deviceId: preferredDevices.videoDeviceId }
        : true

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraint,
        video: videoConstraint,
      })
      clearTimeout(timeout)
      if (this.isDisposed) {
        stream.getTracks().forEach((t) => t.stop())
        return
      }

      this.logEvent('media_permissions_granted')

      const audioTrack = stream.getAudioTracks()[0] ?? null
      const videoTrack = stream.getVideoTracks()[0] ?? null
      const audioId = audioTrack?.getSettings().deviceId
      const videoId = videoTrack?.getSettings().deviceId
      if (audioId)
        sessionStorage.setItem('bl-permission-audio-device-id', audioId)
      if (videoId)
        sessionStorage.setItem('bl-permission-video-device-id', videoId)

      runInAction(() => {
        this.localAudioTrack = audioTrack
        this.localVideoTrack = videoTrack
        this.localAudioDeviceId =
          preferredDevices?.audioDeviceId || audioId || ''
        this.localVideoDeviceId =
          preferredDevices?.videoDeviceId || videoId || ''
        this.mediaPermissionState = 'granted'
        this.audioPermissionState = 'granted'
        this.videoPermissionState = 'granted'
      })
    } catch (e1) {
      clearTimeout(timeout)
      if (this.isDisposed) return

      const err = this._toError(e1)
      this.logEvent('media_permissions_failed', { error_name: err.name })
      this._resolvePermissionError(
        err,
        action((s: DetailedPermissionState) => {
          this.mediaPermissionState = s
        })
      )

      // Try audio and video separately
      await Promise.all([
        this.requestAudioPermission(preferredDevices?.audioDeviceId),
        this.requestVideoPermission(preferredDevices?.videoDeviceId),
      ])
    }
  }

  private async requestAudioPermission(preferredDeviceId?: string) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: preferredDeviceId ? { deviceId: preferredDeviceId } : true,
      })
      if (this.isDisposed) {
        stream.getTracks().forEach((t) => t.stop())
        return
      }

      this.logEvent('media_permissions_audio_granted')

      const audioTrack = stream.getAudioTracks()[0] ?? null
      const audioId = audioTrack?.getSettings().deviceId
      if (audioId)
        sessionStorage.setItem('bl-permission-audio-device-id', audioId)

      runInAction(() => {
        this.localAudioTrack = audioTrack
        this.localAudioDeviceId = preferredDeviceId || audioId || ''
        this.audioPermissionState = 'granted'
        // If we have at least mic access, move forward
        this.mediaPermissionState = 'granted'
      })
    } catch (e) {
      if (this.isDisposed) return
      const err = this._toError(e)
      this.logEvent('media_permissions_audio_failed', { error_name: err.name })
      this._resolvePermissionError(
        err,
        action((s: DetailedPermissionState) => {
          this.audioPermissionState = s
        })
      )
    }
  }

  private async requestVideoPermission(preferredDeviceId?: string) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: preferredDeviceId ? { deviceId: preferredDeviceId } : true,
      })
      if (this.isDisposed) {
        stream.getTracks().forEach((t) => t.stop())
        return
      }

      this.logEvent('media_permissions_video_granted')

      const videoTrack = stream.getVideoTracks()[0] ?? null
      const videoId = videoTrack?.getSettings().deviceId
      if (videoId)
        sessionStorage.setItem('bl-permission-video-device-id', videoId)

      runInAction(() => {
        this.localVideoTrack = videoTrack
        this.localVideoDeviceId = preferredDeviceId || videoId || ''
        this.videoPermissionState = 'granted'
      })
    } catch (e) {
      if (this.isDisposed) return
      const err = this._toError(e)
      this.logEvent('media_permissions_video_failed', { error_name: err.name })
      this._resolvePermissionError(
        err,
        action((s: DetailedPermissionState) => {
          this.videoPermissionState = s
        })
      )
    }
  }

  // ---------------------------------------------------------------------------
  // Device switching
  // ---------------------------------------------------------------------------

  async switchAudioDevice(deviceId: string) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId },
      })
      const track = stream.getAudioTracks()[0] ?? null
      const previousTrack = this.localAudioTrack
      runInAction(() => {
        this.localAudioTrack = track
        this.localAudioDeviceId = deviceId
      })
      previousTrack?.stop()
    } catch {
      // Keep existing track if new device acquisition fails
    }
  }

  async switchVideoDevice(deviceId: string) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId },
      })
      const track = stream.getVideoTracks()[0] ?? null
      const previousTrack = this.localVideoTrack
      runInAction(() => {
        this.localVideoTrack = track
        this.localVideoDeviceId = deviceId
      })
      previousTrack?.stop()
    } catch {
      // Keep existing track if new device acquisition fails
    }
  }

  // ---------------------------------------------------------------------------
  // Cleanup & skip
  // ---------------------------------------------------------------------------

  @action
  stopLocalTracks() {
    this.localAudioTrack?.stop()
    this.localAudioTrack = null
    this.localVideoTrack?.stop()
    this.localVideoTrack = null
  }

  @action
  setLocalAudioDeviceId(deviceId: string) {
    this.localAudioDeviceId = deviceId
  }

  @action
  setLocalVideoDeviceId(deviceId: string) {
    this.localVideoDeviceId = deviceId
  }

  @action
  skipMediaPermissions() {
    this.logEvent('media_permissions_skipped')
    this.mediaPermissionState = 'granted'
    this.audioPermissionState = 'granted'
    this.videoPermissionState = 'granted'
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /** Convert an unknown thrown value to an Error, preserving `name` from DOMException-like objects. */
  private _toError(e: unknown): Error {
    if (e instanceof Error) return e
    const err = new Error(String(e))
    if (typeof e === 'object' && e !== null && 'name' in e) {
      err.name = String((e as { name: unknown }).name)
    }
    return err
  }

  private _resolvePermissionError(
    e: Error,
    setter: (state: DetailedPermissionState) => void
  ) {
    switch (e.name) {
      case 'NotAllowedError':
        setter('NotAllowed')
        break
      case 'NotReadableError':
        setter('NotReadable')
        break
      case 'OverconstrainedError':
        setter('Overconstrained')
        break
      case 'SecurityError':
        setter('Security')
        break
      default:
        setter('denied')
    }
  }
}
